import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog, MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInput, MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { Store } from '@ngxs/store';
import { NotifyStakeholdersModalComponent } from '@shared/modules/approvals/notify-stakeholders-modal/notify-stakeholders-modal.component';
import { TaskStatusService } from '@tasks/services/task-status.service';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { TaskStatus, WorkflowState, Task } from '@tasks/tasks.types';
import { assign } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { TsButtonModule } from 'tsui';
import { DirectivesModule } from '@shared/directives/directives.module';

@Component({
  selector: 'app-task-details-footer',
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyInputModule,
    MatLegacyDialogModule,
    MatLegacyButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatTooltipModule,
    TsButtonModule,
    ReactiveFormsModule,
    DirectivesModule,
  ],
  template: `
    <div [formGroup]="footerForm" class="flex items-center pt-3">
      <div class="footer-status" [formGroup]="footerForm">
        <mat-form-field class="flex-auto gt-xs:pr-3">
          <mat-select
            [formControlName]="'workflow_state'"
            [panelClass]="'task-status-selector'"
            [appAuthorize]="canEditTask"
            denyAction="disable"
          >
            <mat-select-trigger>
              <span
                class="status-color"
                [ngClass]="getTaskStatus(footerForm.get('workflow_state').value)?.color"
              ></span>
              <span class="calendar-title">{{ getTaskStatus(footerForm.get('workflow_state').value)?.label }}</span>
            </mat-select-trigger>
            <mat-option
              *ngFor="let state of workflowStates"
              [value]="state.key"
              [disabled]="state.disabled"
              [matTooltip]="state.disabledReason || ''"
              matTooltipPosition="right"
            >
              <span class="calendar-color" [ngClass]="state.color"></span>
              {{ state.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <ts-button
        [loading]="loadingStatusButton"
        color="success"
        (click)="onUpdateStatusButton()"
        *ngIf="showShortcutButton"
        [appAuthorize]="canEditTask"
        data-dd-action-name="Tasks - Footer - Update Status Button"
        denyAction="disable"
      >
        {{ updateStatusButton }}
      </ts-button>
      <ng-container *ngIf="!showShortcutButton">
        <div>
          <div class="due-date">
            <button
              mat-button
              (click)="openCompletedAtPanel()"
              #completedAtPanelOrigin
              [appAuthorize]="canEditTask"
              denyAction="disable"
            >
              <mat-icon [svgIcon]="'today'"></mat-icon>

              <span *ngIf="task?.completed_at">{{ task?.completed_at | date: 'longDate' }}</span>
              <span *ngIf="!task?.completed_at">Not set</span>
            </button>
          </div>

          <!-- Due date panel -->
          <ng-template #completedAtPanel>
            <div class="tasks-details-due-date-panel bg-white shadow-lg pb-2">
              <mat-calendar
                [selected]="footerForm.get('completed_at').value"
                [startAt]="footerForm.get('completed_at').value"
                (selectedChange)="updateCompletedAt($event)"
              ></mat-calendar>

              <button mat-stroked-button *ngIf="footerForm.get('completed_at').value" (click)="removeCompletedAt()">
                <mat-icon [svgIcon]="'remove_circle'"></mat-icon>
                Remove completion date
              </button>
            </div>
          </ng-template>
        </div>
      </ng-container>
    </div>
  `,
  styleUrl: './task-details-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsFooterComponent implements OnInit {
  private _task: Task;
  @Input() canEditTask: boolean;
  @Input()
  set task(value: Task) {
    this._task = value;
    if (this.footerForm) {
      this.footerForm.patchValue(
        {
          workflow_state: value.workflow_state,
          completed_at: value.completed_at,
        },
        { emitEvent: false },
      );
    }
  }

  get task(): Task {
    return this._task;
  }

  @Output() onUpdateTask = new EventEmitter<Partial<Task>>();

  private taskStatusService = inject(TaskStatusService);
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private overlay = inject(Overlay);
  private cdr = inject(ChangeDetectorRef);
  private matDialog = inject(MatLegacyDialog);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);
  private completedAtPanelTemplatePortal: TemplatePortal;
  private completedAtPanelOverlayRef: OverlayRef;

  @ViewChild('completedAtPanelOrigin')
  private completedAtPanelOrigin: MatButton;

  @ViewChild('completedAtPanel')
  private completedAtPanel: TemplateRef<any>;

  loadingStatusButton = false;

  workflowStates;
  users;

  get hasApprovalPermission(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.TASK_LIST_APPROVALS));
  }

  get showShortcutButton(): boolean {
    if (this.footerForm.get('workflow_state').value === 'complete') {
      return false;
    }
    return true;
  }

  get updateStatusButton(): string {
    return this.taskStatusService.getNextButtonLabel(this.task, this.hasApprovalPermission);
  }

  updateCompletedAt(event: moment.Moment): void {
    // Get the form control for 'dueDate'
    const completedAtFormControl = this.footerForm.get('completed_at');

    // Update it
    completedAtFormControl.setValue(event.toISOString());

    // Close the due date panel...
    this.closeCompletedAtPanel();
  }

  /**
   * Remove the due date
   */
  removeCompletedAt(): void {
    // Get the form control for 'dueDate'
    const completedAtFormControl = this.footerForm.get('completed_at');

    // Clear the due date
    completedAtFormControl.setValue(null);

    // Close the due date panel...
    this.closeCompletedAtPanel();
  }

  ngOnInit(): void {
    this.workflowStates = this.taskStatusService.getAvailableWorkflowStates(this.task);
    this.users = this.store.selectSnapshot(UserState.getUsers);

    this.footerForm = this.fb.group({
      workflow_state: [this.task.workflow_state],
      completed_at: [this.task.completed_at],
    });

    // // Update task when there is a value change on the task form
    this.footerForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((value: { workflow_state: WorkflowState; completed_at: string }) => {
          this.loadingStatusButton = true;
          const updatingStatusToInApproval = value.workflow_state === 'in_approval';

          const skipModal = !this.hasApprovalPermission && this.task.approvals?.length === 0;

          return updatingStatusToInApproval && !skipModal ? this.openModal({ ...this.task, ...value }) : of(value);
        }),
        filter((value) => {
          if (!value) {
            this.footerForm.patchValue(this.task, { emitEvent: false });
            this.loadingStatusButton = false;
            return false;
          }
          return true;
        }),
        debounceTime(300),
      )
      .subscribe((value) => {
        this.loadingStatusButton = false;
        this.task = assign(this.task, value);
        this.onUpdateTask.emit(value);
        // this.saveTask(value);
        // EMIT NEW TASK
      });
  }

  footerForm;

  onUpdateStatusButton(): void {
    this.footerForm
      .get('workflow_state')
      .setValue(this.taskStatusService.getNextStatus(this.task, this.hasApprovalPermission));
  }

  getTaskStatus(status): TaskStatus {
    if (!status) {
      return;
    }

    return this.workflowStates.find((taskStatus) => taskStatus.key === status);
  }

  openModal(task: Task): Observable<any> {
    task = {
      ...task,
      approvals: this.task.approvals || [],
      task_list_id: this.task.task_list_id,
    };

    const dialogRef = this.matDialog.open(NotifyStakeholdersModalComponent, {
      data: { task, users: this.users },
      autoFocus: false,
      restoreFocus: false,
    });

    return dialogRef.afterClosed();
  }

  /**
   * Open due date panel
   */
  openCompletedAtPanel(): void {
    // Create the overlay
    this.completedAtPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.completedAtPanelOrigin?._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(5)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ]),
    });

    // Create a portal from the template
    this.completedAtPanelTemplatePortal = new TemplatePortal(this.completedAtPanel, this.viewContainerRef);

    // Attach the portal to the overlay
    this.completedAtPanelOverlayRef.attach(this.completedAtPanelTemplatePortal);

    // Subscribe to the backdrop click
    this.completedAtPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Close the panel
        this.closeCompletedAtPanel();
      });
  }

  /**
   * Close the due date panel
   */
  closeCompletedAtPanel(): void {
    // If overlay exists and attached...
    if (this.completedAtPanelOverlayRef && this.completedAtPanelOverlayRef.hasAttached()) {
      // Detach it
      this.completedAtPanelOverlayRef.detach();
    }

    // If template portal exists and attached...
    if (this.completedAtPanelTemplatePortal && this.completedAtPanelTemplatePortal.isAttached) {
      // Detach it
      this.completedAtPanelTemplatePortal.detach();
    }

    // Mark for check
    this.cdr.markForCheck();
  }
}
